import { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Header from './Header'
import TrainingProgressBanner from './TrainingProgressBanner'
import ArcWakeWordListener from '@/components/common/ArcWakeWordListener'
import { ToastContainer, SpokeRestrictionToast } from '@/components/common'
import { useApiHealthCheck } from '@/hooks/useApiHealthCheck'
import { useUserProfile } from '@/hooks/useUserProfile'
import { useBrainPageVisits } from '@/hooks/useBrainPageVisits'

interface MainLayoutProps {
  children: ReactNode
}

export default function MainLayout({ children }: MainLayoutProps) {
  // Initialize API health check - updates connection status in header
  useApiHealthCheck()

  // Fetch user profile and set industry from organization
  useUserProfile()

  // Record Brain/Playground page visits for the workflow progress tracker.
  useBrainPageVisits()

  // v30: InvestigationThread breadcrumb removed — cross-page context
  // is now carried by PromoteButton's readPromotePayload + Arc Console
  // seed/origin params, so the thread bar was decorative noise. Store
  // remains in case other surfaces resume using it; UI surfaces are gone.
  const location = useLocation()
  // 2026-07-03 — removed the global WorkflowJourney strip (OBSERVE→UNDERSTAND→
  // ACT→EVOLVE + "N/4 phases · N/30 pages" counter). It duplicated the
  // per-page BrainFlowIndicator (same phase flow, but with page-specific chain
  // context), so every Brain page showed the flow twice — decorative repetition
  // the operator flagged. BrainFlowIndicator is the single phase indicator now.

  return (
    <div className="flex h-screen bg-surface-950">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header />

        {/* Always-on training + platform progress banner.
            Polls /api/v2/public/progress every 8s, shows only when
            the system is training, queued, awaiting data, or needs
            attention. Hidden once everything is ready + dismissed.
            v93: kept as the canonical real-time training indicator on
            every page. Command Center used to render an in-page
            TrainingProgressHero too — that hero is now removed in v93
            to avoid duplication, since the user prefers this persistent
            top bar as the single source of truth. */}
        <TrainingProgressBanner />

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4">
          {/* 2026-07-03 — global WorkflowJourney strip removed (duplicated the
              per-page BrainFlowIndicator). See note above. */}
          <div className="animate-in">
            {children}
          </div>
          {/* 2026-07-03 — global NextStepCard removed. It stacked a second
              footer nav block under the per-page RelatedPagesGrid (both do
              cross-navigation). Consolidated to the single RelatedPagesGrid
              footer nav. */}
        </main>
      </div>

      {/* Toast Notifications */}
      <ToastContainer />
      <SpokeRestrictionToast />

      {/* Global "Hey Arc" wake-word listener. Off by default — opt-in
          via the toggle in Arc Console. Mounted at app root so the
          phrase works on every page, not only inside /arc. */}
      <ArcWakeWordListener />
    </div>
  )
}
